import React, { forwardRef, ReactElement, useEffect, useState } from 'react';
import classNames from 'classnames';

import styles from './TooltipButton.module.scss';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactElement;
  variant?: 'secondary' | 'primary';
  size?: 's' | 'l';
  active?: boolean;
}

const variantClass = {
  secondary:
    'dark:bg-white dark:text-black dark:group-hover:text-white dark:focus-visible:bg-primary dark:focus-visible:text-on-brand opacity-70 bg-black text-white focus-visible:bg-primary focus-visible:text-on-brand group-hover:bg-primary group-hover:text-on-brand',
  primary: 'bg-primary opacity-1 text-on-brand',
};

const variantActiveClass = {
  secondary: 'bg-white',
  primary: 'bg-white',
};

const sizeClass = {
  s: 'size-[20px] p-0',
  l: 'p-[5px]',
};

const ringVariantClass = {
  secondary:
    'dark:border-white dark:focus-visible:bg-white border-black text-white focus-visible:bg-black hover:bg-white hover:border-white',
  primary: 'bg-white border-white',
};

const TooltipButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, icon, variant = 'secondary', size = 'l', active, ...props }, ref) => {
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
      if (active !== undefined) {
        setIsActive(active);
      }
    }, [active]);

    const ringStyle =
      'rounded-full p-1 bg-transparent overflow-hidden border border-solid group transition-all ease-linear duration-300';
    const innerClass = 'rounded-full size-11 flex group-hover:opacity-100';
    return (
      <button
        ref={ref}
        className={classNames(
          ringStyle,
          styles.tooltipButton,
          isActive && variantActiveClass[variant],
          ringVariantClass[variant],
          className
        )}
        aria-pressed={isActive}
        {...props}
      >
        <div
          className={classNames(
            innerClass,
            variantClass[variant],
            sizeClass[size],
            isActive && '!bg-primary !text-on-brand !opacity-100'
          )}
        >
          {icon}
        </div>
      </button>
    );
  }
);

TooltipButton.displayName = 'TooltipButton';

export default TooltipButton;
